<script setup lang="ts">
/**
 * In-chat assessment report (candidate `completion: "report"` mode).
 *
 * When the governing agent opts into an in-chat report, the chat takeover renders this in its
 * `#report` slot instead of redirecting to the server-resolved report page. We keep the candidate in
 * the conversation and render their OWN report inline by mounting the report resource via the normal
 * `resource-renderer` in `embedded` mode (no nested chat).
 *
 * The report resource path + the candidate profile id are derived from the completion `navigation`
 * the server resolved from the assessment's submit block (`<reportPath>?user=<profileId>`). Because the
 * report provider normally reads the target user from `route.query.user` (the report page's own
 * route), we `provide("reportUserId", ...)` so the embedded report resolves the right candidate
 * without a full-page navigation.
 */
import { computed, provide } from "vue";
import { useI18n } from "vue-i18n";

interface CompletionNavigation {
  url: string;
  external: boolean;
}
interface CompletionReportFile {
  path: string;
  type?: string | null;
}
interface CompletionLike {
  reason?: string | null;
  navigation?: CompletionNavigation | null;
  runId?: string | null;
  reportFile?: CompletionReportFile | null;
}

const props = defineProps<{ completion: CompletionLike | null }>();

const { t } = useI18n({ useScope: "local" });
const { profile } = useProfile();

/** Report resource path + target candidate id, derived from the completion navigation. */
const reportTarget = computed<{ path: string; user?: string } | null>(() => {
  const url = props.completion?.navigation?.url;
  if (!url) {
    return null;
  }
  const origin = import.meta.client ? window.location.origin : "http://localhost";
  try {
    const parsed = new URL(url, origin);
    const path = parsed.pathname.replace(/^\/+/, "");
    if (!path) {
      return null;
    }
    const user = parsed.searchParams.get("user") || profile.value?.id || undefined;
    return { path, user };
  } catch {
    return null;
  }
});

// Override the report provider's user resolution so the embedded report shows THIS candidate even
// though the surrounding page route (the assessment) carries no `?user=`.
const reportUserId = computed(() => reportTarget.value?.user);
provide("reportUserId", reportUserId);

const fullReportUrl = computed(() => props.completion?.navigation?.url ?? null);
</script>

<template>
  <div class="space-y-4" data-slot="candidate-assessment-report">
    <div class="rounded-xl border border-primary/30 bg-primary/5 p-4">
      <p class="text-sm font-semibold text-foreground">{{ t("title") }}</p>
      <p v-if="completion?.runId" class="mt-1 text-xs text-muted-foreground">
        {{ t("runId", { id: completion.runId }) }}
      </p>
    </div>

    <resource-renderer
      v-if="reportTarget?.path"
      :key="reportTarget.path"
      :resource-path="reportTarget.path"
      mode="viewer"
      embedded
    />

    <a
      v-else-if="fullReportUrl"
      :href="fullReportUrl"
      class="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
    >
      {{ t("viewReport") }}
    </a>
  </div>
</template>

<i18n lang="yaml">
en:
  title: Assessment complete
  runId: "Run ID: {id}"
  viewReport: View your report
</i18n>
